import {
    useImperativeHandle,
    forwardRef,
    useEffect,
    Fragment,
    useState,
    useMemo,
    type Ref,
    useRef
} from "react";
import {
    type TextInputKeyPressEventData,
    TextInput as NativeTextInput,
    type NativeSyntheticEvent,
    Pressable,
    Platform,
    View
} from "react-native";
import {
    type SelectBoxTriggerAccessibilityType,
    type SelectBoxTriggerWebPropsType,
    type SelectBoxKeyboardEventType,
    type SelectBoxInputInstance,
    type SelectBoxComponent,
    type ISelectBoxRef,
    type SelectedItem
} from "./type";
import type ISelectBoxProps from "./type";
import stylesheet, {
    getIsSameSelectBoxSelection,
    getSelectBoxMappedSelection,
    getIsSelectBoxSyntheticItem,
    getSelectBoxPreparedData,
    getSelectBoxType,
    useStyles
} from "./stylesheet";
import SelectPopover from "./components/selectPopover";
import {
    getSelectBoxControlElement,
    getSelectBoxValueItems,
    getIsSameSelectBoxData,
    getSelectBoxValueKeys,
    getSelectBoxValue
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    NCoreUIKitToast
} from "../../core/hooks";
import {
    type NCoreUIKitIcon
} from "../../types";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    usePointerType
} from "../../helpers/pointer";
import type {
    IBottomSheetRef
} from "../bottomSheet/type";
import {
    type FieldInputAccessibilityType,
    type FieldInputRenderPropsType,
    type FieldActionType
} from "../fieldControl/type";
import {
    ChevronDown as ChevronDownIcon,
    ChevronUp as ChevronUpIcon,
    Search as SearchIcon
} from "lucide-react-native";
import {
    getFieldIconColor,
    getFieldSizeKey,
    getFieldColors,
    getFieldSize
} from "../fieldControl/stylesheet";
import {
    COMBOBOX_PAGE_ROWS,
    getComboboxFold,
    getComboboxMove
} from "../combobox/util";
import {
    getDropdownMenuTypeaheadIndex
} from "../dropdownMenu/util";
import {
    getSelectSheetSelection
} from "../selectSheet/stylesheet";
import {
    getSelectionGroups
} from "../../helpers/selection";
import {
    FieldClearIcon
} from "../fieldControl/icons";
import {
    warnOnce
} from "../../utils/development";
import {
    uuid
} from "../../utils";
import FieldControl, {
    FieldAction
} from "../fieldControl";
import AnimatedIcon from "../animatedIcon";
import SelectSheet from "../selectSheet";
import Text from "../text";

const SearchFieldIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => <SearchIcon
    color={customColor}
    size={size}
/>;

function SelectBox<T>({
    renderLoadingIcon: LoadingIconComponentProp,
    rightIcon: RightIconComponentProp,
    isShowSelectSheetTools = true,
    spreadBehaviour = "baseline",
    rightIconAccessibilityLabel,
    isShowHintTextIcon = false,
    customSelectSheetLocalize,
    isWorkWithRealtime = true,
    isMinWidthEnabled = true,
    isMultipleSelect = false,
    icon: IconComponentProp,
    customSelectSheetTheme,
    hintTextContainerStyle,
    iconAccessibilityLabel,
    removeSelectValidation,
    contentContainerStyle,
    CustomPrefixComponent,
    initialSelectedItems,
    isSearchable = false,
    onFocus: onFocusProp,
    accessibilityLabel,
    isDisabled = false,
    isReadOnly = false,
    isRequired = false,
    onBlur: onBlurProp,
    data: initialData,
    hintTextIconStyle,
    isLoading = false,
    moreLoadThreshold,
    renderOptionIcon,
    rightIconOnPress,
    selectSheetProps,
    selectValidation,
    type = "neutral",
    cleanIconStyle,
    clearIconStyle,
    customLocalize,
    groupExtractor,
    isCleanEnabled,
    isClearEnabled,
    isShowSubTitle,
    maxChoice = -1,
    rightIconStyle,
    titleExtractor,
    onValueChange,
    contentStyle,
    defaultValue,
    hintTextIcon,
    keyExtractor,
    customTheme,
    iconOnPress,
    placeholder,
    isOptional,
    onMoreLoad,
    renderItem,
    customKey,
    iconStyle,
    minChoice,
    hintText,
    onCancel,
    onChange,
    onSearch,
    subTitle,
    style,
    title,
    value,
    onOk,
    size,
    id
}: ISelectBoxProps<T>, ref: Ref<ISelectBoxRef<T>>) {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        typography,
        durations,
        iconSizes,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const bottomSheetRef = useRef<IBottomSheetRef>(null);

    const readOnlyInputRef = useRef<SelectBoxInputInstance | null>(null);

    const typeaheadBufferRef = useRef("");

    const typeaheadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const pendingActiveKeyRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        return () => {
            if(typeaheadTimerRef.current !== null) {
                clearTimeout(typeaheadTimerRef.current);
            }
        };
    }, []);

    const triggerRef = useRef<View>(null);

    const anchorRef = useRef<View>(null);

    const searchRef = useRef<SelectBoxInputInstance>(null);

    const isKeySelectRef = useRef(false);

    const isControlled = value !== undefined;

    const selectBoxKey = useRef(customKey ? customKey : uuid());

    const generatedID = useRef(uuid());

    const [
        seedSelectedItems
    ] = useState<Array<SelectedItem>>(() => {
        const seedValue = value ?? defaultValue;

        if(seedValue === undefined) {
            return initialSelectedItems ?? [];
        }

        return getSelectBoxValueItems({
            data: initialData.map((item, index) => ({
                __title: titleExtractor(item as T & SelectedItem, index),
                __key: keyExtractor(item as T & SelectedItem, index)
            })),
            keys: getSelectBoxValueKeys(seedValue),
            known: initialSelectedItems ?? []
        });
    });

    const [
        data,
        setData
    ] = useState<Array<T & SelectedItem | SelectedItem>>(() => getSelectBoxPreparedData({
        items: initialData as Array<T & SelectedItem>,
        selectedItems: seedSelectedItems,
        titleExtractor,
        keyExtractor
    }));

    const [
        searchedData,
        setSearchedData
    ] = useState<Array<T & SelectedItem | SelectedItem>>([]);

    const [
        isActive,
        setIsActive
    ] = useState(false);

    const [
        activeKey,
        setActiveKey
    ] = useState<string | undefined>(undefined);

    const [
        searchText,
        setSearchText
    ] = useState("");

    const [
        isListLoading,
        setIsListLoading
    ] = useState(false);

    const [
        isMoreLoading,
        setIsMoreLoading
    ] = useState(false);

    const [
        isReadOnlyFocused,
        setIsReadOnlyFocused
    ] = useState(false);

    const [
        selectedItemsState,
        setSelectedItems
    ] = useState<Array<SelectedItem>>(seedSelectedItems);

    const [
        tempSelectedItems,
        setTempSelectedItems
    ] = useState<Array<SelectedItem>>(seedSelectedItems);

    const dataRef = useRef(data);

    const dataPropRef = useRef(initialData);

    const selectedItemsRef = useRef(selectedItemsState);

    const valueSignature = isControlled ? JSON.stringify(getSelectBoxValueKeys(value)) : undefined;

    const controlledSelectedItems = useMemo(() => {
        if(valueSignature === undefined) {
            return undefined;
        }

        return getSelectBoxValueItems({
            keys: JSON.parse(valueSignature) as Array<string>,
            known: selectedItemsRef.current,
            data
        });
    }, [
        valueSignature,
        data
    ]);

    const selectedItems = controlledSelectedItems ?? selectedItemsState;

    if(controlledSelectedItems) {
        selectedItemsRef.current = controlledSelectedItems;
    }

    const tempSelectedItemsRef = useRef(tempSelectedItems);

    const isActiveRef = useRef(isActive);

    const previousIsActive = useRef(isActive);

    const inputID = id ?? generatedID.current;

    const valueID = `${inputID}-value`;

    const listboxID = `${inputID}-listbox`;

    const isCoarse = pointerType === "coarse";

    const isPopoverOpen = isActive && !isCoarse;

    const mainSelectedItems = isWorkWithRealtime ? selectedItems : tempSelectedItems;

    const getItemGroup = groupExtractor ? (item: T & SelectedItem | SelectedItem) => getIsSelectBoxSyntheticItem(item) ? "" : groupExtractor(item as T) : undefined;

    const baseOptions = searchText.length ? searchedData : data;

    const options = getItemGroup ? getSelectionGroups({
        getHeading: (item) => getItemGroup(item) || undefined,
        getKey: (item) => getItemGroup(item) ?? "",
        entries: baseOptions
    }).flatMap((group) => group.options) : baseOptions;

    const currentSelection = getSelectSheetSelection({
        selectedItems: mainSelectedItems,
        maxChoice,
        minChoice
    });

    const enabledKeys = options
        .filter((item) => !isMultipleSelect || currentSelection.selectedKeys.has(item.__key) || !currentSelection.isMaxChoiceReached)
        .map((item) => item.__key);

    const optionsSignature = options.map((item) => item.__key).join("|");

    const isCurrentClearEnabled = (isClearEnabled ?? isCleanEnabled ?? false) && !(isMultipleSelect && minChoice);

    const isHintTextIconStyleUsed = hintTextIconStyle !== undefined;

    const isCleanIconStyleUsed = cleanIconStyle !== undefined;

    const isRightIconPressable = !!rightIconOnPress;

    const isIconPressable = !!iconOnPress;

    const isInteractive = !isDisabled && !isReadOnly && !isLoading;

    const currentType = getSelectBoxType({
        type
    });

    const currentSizeKey = getFieldSizeKey({
        isActionable: isCurrentClearEnabled || isIconPressable || isRightIconPressable,
        pointerType,
        size
    });

    const currentSize = getFieldSize({
        size: currentSizeKey,
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        pointerType,
        typography,
        iconSizes,
        spaces
    });

    const currentColors = getFieldColors({
        type: currentType,
        isDisabled,
        isReadOnly,
        colors
    });

    const {
        interactionHandlers,
        isFocusVisible
    } = useInteractionState({
        isDisabled: isDisabled || isReadOnly
    });

    const {
        chevron: chevronDynamicStyle,
        trigger: triggerDynamicStyle,
        icon: iconDynamicStyle
    } = useStyles({
        isInteractive,
        currentSize,
        isLoading
    });

    useImperativeHandle(
        ref,
        () => ({
            setIsMoreLoading: (state) => setIsMoreLoading(state),
            setIsLoading: (state) => setIsListLoading(state),
            updateSelections,
            cleanSelections,
            clearSelections,
            updateData,
            selectAll,
            cleanAll,
            clearAll,
            cancel,
            focus,
            blur,
            ok
        })
    );

    useEffect(() => {
        if(previousIsActive.current === isActive) {
            return;
        }

        previousIsActive.current = isActive;

        isActiveRef.current = isActive;

        if(isActive) {
            if(onFocusProp) {
                onFocusProp();
            }

            return;
        }

        handleSheetClose();
    }, [
        isActive
    ]);

    useEffect(() => {
        if(!isPopoverOpen) {
            return;
        }

        const selectedKey = enabledKeys.find((key) => currentSelection.selectedKeys.has(key));

        setActiveKey(searchText.trim().length ? enabledKeys[0] : pendingActiveKeyRef.current ?? selectedKey ?? enabledKeys[0]);

        pendingActiveKeyRef.current = undefined;
    }, [
        optionsSignature,
        isPopoverOpen,
        searchText
    ]);

    useEffect(() => {
        const elementID = getOptionID(activeKey);

        if(Platform.OS !== "web" || !isPopoverOpen || !elementID || typeof globalThis.document === "undefined") {
            return;
        }

        const element = globalThis.document.getElementById(elementID);

        if(typeof element?.scrollIntoView !== "function") {
            return;
        }

        element.scrollIntoView({
            block: "nearest"
        });
    }, [
        isPopoverOpen,
        activeKey
    ]);

    useEffect(() => {
        if(getIsSameSelectBoxData({
            current: dataPropRef.current,
            next: initialData
        })) {
            return;
        }

        dataPropRef.current = initialData;

        updateData(initialData);
    }, [
        initialData
    ]);

    useEffect(() => {
        if(isSearchable && searchText.length) {
            if(onSearch) {
                setSearchedData(getSelectBoxPreparedData({
                    items: onSearch({
                        selectedItems: mainSelectedItems,
                        setIsLoading: setIsListLoading,
                        setIsMoreLoading,
                        searchText,
                        data
                    }) as Array<T & SelectedItem>,
                    selectedItems: mainSelectedItems,
                    titleExtractor,
                    keyExtractor
                }));

                return;
            }

            const foldedSearchText = getComboboxFold({
                locale: activeLocale,
                text: searchText
            });

            setSearchedData(data.filter((item) => getComboboxFold({
                locale: activeLocale,
                text: item.__title
            }).includes(foldedSearchText)));

            return;
        }

        if(searchedData.length) {
            setSearchedData([]);
        }
    }, [
        searchText,
        data
    ]);

    useEffect(() => {
        if(type !== "default") {
            return;
        }

        warnOnce({
            message: "SelectBox: type \"default\" is deprecated and will be removed in the next major version. Use \"neutral\" instead."
        });
    }, [
        type
    ]);

    useEffect(() => {
        if(type !== "question") {
            return;
        }

        warnOnce({
            message: "SelectBox: type \"question\" is deprecated and will be removed in the next major version. Use type \"neutral\" with \"isShowHintTextIcon\" instead."
        });
    }, [
        type
    ]);

    useEffect(() => {
        if(!isControlled || onValueChange) {
            return;
        }

        warnOnce({
            message: "SelectBox: a controlled \"value\" needs an \"onValueChange\" handler that updates it."
        });
    }, [
        onValueChange,
        isControlled
    ]);

    useEffect(() => {
        if(initialSelectedItems === undefined) {
            return;
        }

        warnOnce({
            message: "SelectBox: \"initialSelectedItems\" is deprecated and will be removed in the next major version. Use \"defaultValue\" instead."
        });
    }, [
        initialSelectedItems
    ]);

    useEffect(() => {
        if(isCleanEnabled === undefined) {
            return;
        }

        warnOnce({
            message: "SelectBox: \"isCleanEnabled\" is deprecated and will be removed in the next major version. Use \"isClearEnabled\" instead."
        });
    }, [
        isCleanEnabled
    ]);

    useEffect(() => {
        if(!isCleanIconStyleUsed) {
            return;
        }

        warnOnce({
            message: "SelectBox: \"cleanIconStyle\" is deprecated and will be removed in the next major version. Use \"clearIconStyle\" instead."
        });
    }, [
        isCleanIconStyleUsed
    ]);

    useEffect(() => {
        if(!isHintTextIconStyleUsed) {
            return;
        }

        warnOnce({
            message: "SelectBox: \"hintTextIconStyle\" is deprecated and has no effect. The helper icon follows the field's helper row."
        });
    }, [
        isHintTextIconStyleUsed
    ]);

    useEffect(() => {
        if(!isIconPressable || iconAccessibilityLabel) {
            return;
        }

        warnOnce({
            message: "SelectBox: a pressable \"icon\" (iconOnPress) needs an \"iconAccessibilityLabel\" so screen readers can announce it."
        });
    }, [
        iconAccessibilityLabel,
        isIconPressable
    ]);

    useEffect(() => {
        if(!isRightIconPressable || rightIconAccessibilityLabel) {
            return;
        }

        warnOnce({
            message: "SelectBox: a pressable \"rightIcon\" (rightIconOnPress) needs a \"rightIconAccessibilityLabel\" so screen readers can announce it."
        });
    }, [
        rightIconAccessibilityLabel,
        isRightIconPressable
    ]);

    const updateIsActive = (nextIsActive: boolean) => {
        isActiveRef.current = nextIsActive;

        setIsActive(nextIsActive);
    };

    const updateDataState = (nextData: Array<T & SelectedItem | SelectedItem>) => {
        dataRef.current = nextData;

        setData(nextData);
    };

    const updateSelectedItems = (nextSelectedItems: Array<SelectedItem>) => {
        if(isControlled) {
            return;
        }

        selectedItemsRef.current = nextSelectedItems;

        setSelectedItems(nextSelectedItems);
    };

    const updateTempSelectedItems = (nextSelectedItems: Array<SelectedItem>) => {
        tempSelectedItemsRef.current = nextSelectedItems;

        setTempSelectedItems(nextSelectedItems);
    };

    const getMainSelectedItems = () => {
        return isWorkWithRealtime ? selectedItemsRef.current : tempSelectedItemsRef.current;
    };

    const notifyChange = (nextSelectedItems: Array<SelectedItem>) => {
        if(onChange) {
            onChange(nextSelectedItems, dataRef.current, setIsMoreLoading, setIsListLoading);
        }
    };

    const notifyValueChange = (nextSelectedItems: Array<SelectedItem>) => {
        if(onValueChange) {
            onValueChange(getSelectBoxValue({
                selectedItems: nextSelectedItems,
                isMultipleSelect
            }), nextSelectedItems);
        }
    };

    const commitSelection = (nextSelectedItems: Array<SelectedItem>): boolean => {
        const currentSelectedItems = getMainSelectedItems();

        const committedSelectedItems = selectedItemsRef.current;

        if(isWorkWithRealtime || !isActiveRef.current) {
            updateSelectedItems(nextSelectedItems);

            if(!getIsSameSelectBoxSelection({
                current: committedSelectedItems,
                next: nextSelectedItems
            })) {
                notifyValueChange(nextSelectedItems);
            }

            if(!isWorkWithRealtime) {
                updateTempSelectedItems(nextSelectedItems);
            }
        } else {
            updateTempSelectedItems(nextSelectedItems);
        }

        if(getIsSameSelectBoxSelection({
            current: currentSelectedItems,
            next: nextSelectedItems
        })) {
            return false;
        }

        notifyChange(nextSelectedItems);

        return true;
    };

    const focusTrigger = () => {
        if(Platform.OS !== "web") {
            return;
        }

        triggerRef.current?.focus();
    };

    const handleSheetClose = () => {
        if(!isWorkWithRealtime) {
            const pendingSelectedItems = tempSelectedItemsRef.current;

            updateTempSelectedItems(selectedItemsRef.current);

            if(!getIsSameSelectBoxSelection({
                current: selectedItemsRef.current,
                next: pendingSelectedItems
            })) {
                notifyChange(selectedItemsRef.current);
            }
        }

        setSearchText("");

        setSearchedData([]);

        setActiveKey(undefined);

        setIsMoreLoading(false);

        if(onBlurProp) {
            onBlurProp();
        }
    };

    const open = (seed?: string) => {
        if(!isWorkWithRealtime) {
            updateTempSelectedItems(selectedItemsRef.current);
        }

        if(seed !== undefined) {
            setSearchText(seed);
        }

        anchorRef.current = getSelectBoxControlElement({
            node: triggerRef.current
        });

        updateIsActive(true);
    };

    const focus = () => {
        if(isDisabled || isReadOnly) {
            return;
        }

        open();
    };

    const blur = () => {
        updateIsActive(false);
    };

    const closeSurface = () => {
        if(isCoarse) {
            bottomSheetRef.current?.close(() => {
                blur();
            });

            return;
        }

        blur();
    };

    const cancel = () => {
        if(isWorkWithRealtime) {
            return;
        }

        const pendingSelectedItems = tempSelectedItemsRef.current;

        closeSurface();

        if(onCancel) {
            onCancel(pendingSelectedItems, dataRef.current, setIsMoreLoading, setIsListLoading);
        }
    };

    const ok = () => {
        if(isWorkWithRealtime) {
            return;
        }

        const pendingSelectedItems = tempSelectedItemsRef.current;

        const committedSelectedItems = selectedItemsRef.current;

        updateSelectedItems(pendingSelectedItems);

        if(!getIsSameSelectBoxSelection({
            current: committedSelectedItems,
            next: pendingSelectedItems
        })) {
            notifyValueChange(pendingSelectedItems);
        }

        closeSurface();

        if(onOk) {
            onOk(pendingSelectedItems, dataRef.current, setIsMoreLoading, setIsListLoading);
        }
    };

    const clearSelections = () => {
        const currentSelectedItems = getMainSelectedItems();

        const committedSelectedItems = selectedItemsRef.current;

        updateSelectedItems([]);

        if(!isWorkWithRealtime) {
            updateTempSelectedItems([]);
        }

        if(committedSelectedItems.length) {
            notifyValueChange([]);
        }

        if(currentSelectedItems.length) {
            notifyChange([]);
        }
    };

    const cleanSelections = () => {
        warnOnce({
            message: "SelectBox: ref method \"cleanSelections\" is deprecated and will be removed in the next major version. Use \"clearSelections\" instead."
        });

        clearSelections();
    };

    const clearAll = () => {
        if(minChoice) {
            warnOnce({
                message: "SelectBox: \"clearAll\" needs \"minChoice\" to be 0 or undefined."
            });

            return;
        }

        commitSelection([]);
    };

    const cleanAll = () => {
        warnOnce({
            message: "SelectBox: ref method \"cleanAll\" is deprecated and will be removed in the next major version. Use \"clearAll\" instead."
        });

        clearAll();
    };

    const selectAll = () => {
        if(maxChoice !== -1) {
            warnOnce({
                message: "SelectBox: \"selectAll\" needs \"maxChoice\" to be -1 (no limit)."
            });

            return;
        }

        const currentSelectedItems = getMainSelectedItems();

        const sourceItems = searchText.length ? searchedData : dataRef.current;

        const addedItems = sourceItems
            .filter((item) => currentSelectedItems.findIndex((selectedItem) => selectedItem.__key === item.__key) === -1)
            .map((item) => ({
                __title: item.__title,
                __key: item.__key
            }));

        commitSelection([
            ...currentSelectedItems,
            ...addedItems
        ]);
    };

    const updateData = (newData: Array<T>, newSelectedItems?: Array<SelectedItem>) => {
        const nextData = newData.map((item, index) => {
            return {
                ...item,
                __title: titleExtractor(item as T & SelectedItem, index),
                __key: keyExtractor(item as T & SelectedItem, index)
            };
        });

        updateDataState(nextData);

        commitSelection(getSelectBoxMappedSelection({
            selectedItems: newSelectedItems ?? getMainSelectedItems(),
            data: nextData
        }));
    };

    const updateSelections = (newSelectedItems: Array<SelectedItem>) => {
        commitSelection(getSelectBoxMappedSelection({
            selectedItems: newSelectedItems,
            data: dataRef.current
        }));
    };

    const selectObject = (selectedItem: T & SelectedItem | SelectedItem): boolean => {
        const currentSelectedItems = getMainSelectedItems();

        const isAlreadySelected = currentSelectedItems.findIndex((item) => item.__key === selectedItem.__key) !== -1;

        if(isAlreadySelected) {
            let isProcessPerm = false;

            if(isMultipleSelect) {
                isProcessPerm = minChoice ? currentSelectedItems.length > minChoice : true;
            } else if(minChoice && minChoice > 0) {
                NCoreUIKitToast.open({
                    title: localize("minimum-one-item-selection-required")
                });
            } else {
                isProcessPerm = !!removeSelectValidation;
            }

            if(!isProcessPerm || removeSelectValidation && !removeSelectValidation(selectedItem)) {
                return false;
            }

            return commitSelection(currentSelectedItems.filter((item) => item.__key !== selectedItem.__key));
        }

        if(isMultipleSelect && maxChoice !== -1 && currentSelectedItems.length >= maxChoice) {
            NCoreUIKitToast.open({
                title: localize("maximum-selection-limit-has-been-reached")
            });

            return false;
        }

        if(selectValidation && !selectValidation(selectedItem)) {
            return false;
        }

        const nextSelectedItem = {
            __title: selectedItem.__title,
            __key: selectedItem.__key
        };

        return commitSelection(isMultipleSelect ? [
            ...currentSelectedItems,
            nextSelectedItem
        ] : [
            nextSelectedItem
        ]);
    };

    const onClearPress = () => {
        clearSelections();

        focusTrigger();
    };

    const onTriggerPress = () => {
        if(!isInteractive) {
            return;
        }

        focusTrigger();

        if(isPopoverOpen) {
            blur();

            return;
        }

        open();
    };

    const getOptionID = (key?: string) => {
        const index = key === undefined ? -1 : options.findIndex((item) => item.__key === key);

        return index === -1 ? undefined : `${listboxID}-${index}`;
    };

    const handleSelectOption = (item: SelectedItem) => {
        const previousSelectedItems = getMainSelectedItems();

        const isChanged = selectObject(item);

        if(isMultipleSelect) {
            return;
        }

        if(!isChanged) {
            if(previousSelectedItems.some((previousItem) => previousItem.__key === item.__key)) {
                closeSurface();
            }

            return;
        }

        if(isWorkWithRealtime) {
            blur();

            return;
        }

        ok();
    };

    const handleApply = () => {
        if(isWorkWithRealtime) {
            blur();

            return;
        }

        ok();
    };

    const handleMoveActive = (delta: number) => {
        setActiveKey(getComboboxMove({
            keys: enabledKeys,
            activeKey,
            delta
        }));
    };

    const handleActivateCurrent = () => {
        const option = options.find((item) => item.__key === activeKey);

        if(option) {
            handleSelectOption(option);
        }
    };

    const handleTabClose = () => {
        if(isMultipleSelect && !isWorkWithRealtime) {
            ok();

            return;
        }

        blur();
    };

    const onSearchKeyPress = (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
        const key = event.nativeEvent.key;

        if(key === "ArrowDown" || key === "ArrowUp") {
            event.preventDefault();

            handleMoveActive(key === "ArrowDown" ? 1 : -1);

            return;
        }

        if(key === "PageDown" || key === "PageUp") {
            event.preventDefault();

            handleMoveActive(key === "PageDown" ? COMBOBOX_PAGE_ROWS : -COMBOBOX_PAGE_ROWS);

            return;
        }

        if(key === "Enter") {
            event.preventDefault();

            handleActivateCurrent();

            return;
        }

        if(key === "Tab") {
            handleTabClose();
        }
    };

    const getTypeaheadKey = (key: string) => {
        const buffer = `${typeaheadBufferRef.current}${key.toLocaleLowerCase(activeLocale)}`;

        const enabledOptions = options.filter((item) => enabledKeys.includes(item.__key));

        const currentKey = isPopoverOpen ? activeKey : enabledKeys.find((enabledKey) => currentSelection.selectedKeys.has(enabledKey));

        const index = getDropdownMenuTypeaheadIndex({
            textValues: enabledOptions.map((item) => String(item.__title ?? "").toLocaleLowerCase(activeLocale)),
            currentIndex: enabledOptions.findIndex((item) => item.__key === currentKey),
            buffer
        });

        typeaheadBufferRef.current = buffer;

        if(typeaheadTimerRef.current !== null) {
            clearTimeout(typeaheadTimerRef.current);
        }

        typeaheadTimerRef.current = setTimeout(() => {
            typeaheadBufferRef.current = "";

            typeaheadTimerRef.current = null;
        }, durations.typeahead);

        return index === -1 ? undefined : enabledOptions[index]?.__key;
    };

    const onTriggerKeyDown = (event: SelectBoxKeyboardEventType) => {
        if(!isInteractive || event.altKey || event.ctrlKey || event.metaKey) {
            return;
        }

        const key = event.key;

        if(!isPopoverOpen) {
            if(key === "ArrowDown" || key === "ArrowUp") {
                event.preventDefault();

                open();

                return;
            }

            if(isSearchable && key && key.length === 1 && key !== " ") {
                event.preventDefault();

                open(key);

                return;
            }

            if(key && key.length === 1 && key !== " ") {
                event.preventDefault();

                const typeaheadKey = getTypeaheadKey(key);

                if(typeaheadKey !== undefined) {
                    pendingActiveKeyRef.current = typeaheadKey;

                    open();
                }
            }

            return;
        }

        if(!isSearchable && key && key.length === 1 && key !== " ") {
            event.preventDefault();

            const typeaheadKey = getTypeaheadKey(key);

            if(typeaheadKey !== undefined) {
                setActiveKey(typeaheadKey);
            }

            return;
        }

        if(key === "ArrowDown" || key === "ArrowUp") {
            event.preventDefault();

            handleMoveActive(key === "ArrowDown" ? 1 : -1);

            return;
        }

        if(key === "PageDown" || key === "PageUp") {
            event.preventDefault();

            handleMoveActive(key === "PageDown" ? COMBOBOX_PAGE_ROWS : -COMBOBOX_PAGE_ROWS);

            return;
        }

        if(key === "Home" || key === "End") {
            event.preventDefault();

            setActiveKey(key === "Home" ? enabledKeys[0] : enabledKeys[enabledKeys.length - 1]);

            return;
        }

        if(key === "Enter" || key === " ") {
            event.preventDefault();

            isKeySelectRef.current = key === " ";

            handleActivateCurrent();

            return;
        }

        if(key === "Tab") {
            handleTabClose();
        }
    };

    const onTriggerKeyUp = (event: SelectBoxKeyboardEventType) => {
        if(event.key !== " " || !isKeySelectRef.current) {
            return;
        }

        event.preventDefault();

        isKeySelectRef.current = false;
    };

    const focusReadOnlyInput = () => {
        readOnlyInputRef.current?.focus();
    };

    const onReadOnlyFocus = () => {
        setIsReadOnlyFocused(true);
    };

    const onReadOnlyBlur = () => {
        setIsReadOnlyFocused(false);
    };

    const getDisplayText = () => {
        if(!selectedItems.length) {
            return undefined;
        }

        if(selectedItems.length > 1) {
            return localize("selected-options-with-count", [
                selectedItems.length
            ]);
        }

        return selectedItems[0]?.__title;
    };

    const getPlaceholder = () => {
        return placeholder ?? localize("select-an-option");
    };

    const getSubTitle = () => {
        if(isOptional) {
            return isRequired ? undefined : localize("is-optional");
        }

        if(!isShowSubTitle) {
            return undefined;
        }

        return subTitle ?? localize("is-optional");
    };

    const getFocusRequest = () => {
        if(isReadOnly) {
            return focusReadOnlyInput;
        }

        return isInteractive ? onTriggerPress : undefined;
    };

    const getStartAction = (): FieldActionType | undefined => {
        if(CustomPrefixComponent) {
            return undefined;
        }

        if(IconComponentProp) {
            return {
                accessibilityLabel: iconAccessibilityLabel,
                icon: IconComponentProp,
                onPress: iconOnPress,
                style: iconStyle
            };
        }

        if(!isSearchable) {
            return undefined;
        }

        return {
            icon: SearchFieldIcon
        };
    };

    const getTriggerAccessibilityProps = (accessibilityProps: FieldInputAccessibilityType, displayText?: string): SelectBoxTriggerAccessibilityType => {
        if(Platform.OS === "web") {
            const popoverProps: SelectBoxTriggerWebPropsType = isCoarse ? {} : {
                "aria-activedescendant": isPopoverOpen && !isSearchable ? getOptionID(activeKey) : undefined,
                "aria-controls": isPopoverOpen ? listboxID : undefined,
                onKeyDown: onTriggerKeyDown,
                onKeyUp: onTriggerKeyUp
            };

            return {
                ...accessibilityProps,
                ...popoverProps,
                "aria-labelledby": accessibilityProps["aria-labelledby"] ? `${accessibilityProps["aria-labelledby"]} ${valueID}` : undefined,
                "aria-haspopup": "listbox"
            };
        }

        if(Platform.OS !== "ios" || !title) {
            return accessibilityProps;
        }

        return {
            ...accessibilityProps,
            accessibilityValue: {
                text: displayText ?? getPlaceholder()
            }
        };
    };

    const renderRightIcon = () => {
        if(!RightIconComponentProp) {
            return null;
        }

        if(rightIconOnPress) {
            return <FieldAction
                accessibilityLabel={rightIconAccessibilityLabel}
                icon={RightIconComponentProp}
                currentColors={currentColors}
                onPress={rightIconOnPress}
                currentSize={currentSize}
                customTheme={customTheme}
                isDisabled={isDisabled}
                style={rightIconStyle}
                isMultiline={false}
            />;
        }

        return <View
            style={[
                rightIconStyle,
                stylesheet.icon,
                iconDynamicStyle
            ]}
            aria-hidden={true}
        >
            <RightIconComponentProp
                color={currentColors.iconColorKey}
                customColor={getFieldIconColor({
                    currentColors,
                    isDisabled,
                    colors
                })}
                size={currentSize.iconSize}
            />
        </View>;
    };

    const renderEndAction = () => {
        if(!isCurrentClearEnabled || !isInteractive || !selectedItems.length && RightIconComponentProp) {
            return renderRightIcon();
        }

        return <FieldAction
            accessibilityLabel={localize("clear-selection")}
            style={clearIconStyle ?? cleanIconStyle}
            isVisible={selectedItems.length > 0}
            currentColors={currentColors}
            currentSize={currentSize}
            customTheme={customTheme}
            onPress={onClearPress}
            icon={FieldClearIcon}
            isMultiline={false}
            isDisabled={false}
        />;
    };

    const renderChevron = () => {
        if(isLoading) {
            return null;
        }

        return <View
            style={[
                stylesheet.chevron,
                chevronDynamicStyle
            ]}
            aria-hidden={true}
        >
            <AnimatedIcon
                customColor={currentColors.iconColor}
                size={currentSize.iconSize}
                fromIcon={ChevronDownIcon}
                isTransitioned={isActive}
                toIcon={ChevronUpIcon}
                iconTransition="morph"
            />
        </View>;
    };

    const renderTrigger = ({
        style: inputDynamicStyle,
        placeholderTextColor,
        accessibilityProps
    }: FieldInputRenderPropsType) => {
        const displayText = getDisplayText();

        const prefix = CustomPrefixComponent ? CustomPrefixComponent() : null;

        if(isReadOnly) {
            return <Fragment>
                {prefix}
                <NativeTextInput
                    {...accessibilityProps}
                    placeholderTextColor={placeholderTextColor}
                    underlineColorAndroid="transparent"
                    placeholder={getPlaceholder()}
                    style={[
                        stylesheet.readOnlyInput,
                        inputDynamicStyle
                    ]}
                    onFocus={onReadOnlyFocus}
                    value={displayText ?? ""}
                    onBlur={onReadOnlyBlur}
                    ref={readOnlyInputRef}
                    multiline={false}
                    editable={false}
                />
            </Fragment>;
        }

        return <Fragment>
            {prefix}
            <Pressable
                {...getTriggerAccessibilityProps(accessibilityProps, displayText)}
                {...interactionHandlers}
                onPress={onTriggerPress}
                aria-expanded={isActive}
                style={[
                    contentStyle,
                    stylesheet.trigger,
                    triggerDynamicStyle
                ]}
                disabled={isDisabled}
                ref={triggerRef}
                role="button"
            >
                <Text
                    customColor={displayText ? currentColors.valueColor : placeholderTextColor}
                    variant={currentSize.valueVariant}
                    customTheme={customTheme}
                    style={stylesheet.value}
                    numberOfLines={1}
                    id={valueID}
                >
                    {displayText ?? getPlaceholder()}
                </Text>
            </Pressable>
            {renderEndAction()}
            {renderChevron()}
        </Fragment>;
    };

    return <Fragment>
        <FieldControl
            isShowHintTextIcon={isShowHintTextIcon || type === "question"}
            isFocused={isActive || isFocusVisible || isReadOnlyFocused}
            hintTextContainerStyle={hintTextContainerStyle}
            accessibilityLabel={accessibilityLabel}
            isMinWidthEnabled={isMinWidthEnabled}
            controlStyle={contentContainerStyle}
            onFocusRequest={getFocusRequest()}
            spreadBehaviour={spreadBehaviour}
            customLocalize={customLocalize}
            startAction={getStartAction()}
            hintTextIcon={hintTextIcon}
            renderInput={renderTrigger}
            customTheme={customTheme}
            subTitle={getSubTitle()}
            isReadOnly={isReadOnly}
            isDisabled={isDisabled}
            isRequired={isRequired}
            isLoading={isLoading}
            size={currentSizeKey}
            hintText={hintText}
            type={currentType}
            inputID={inputID}
            title={title}
            style={style}
        />
        {
            isCoarse ?
                <SelectSheet
                    customLocalize={customSelectSheetLocalize ?? customLocalize}
                    customTheme={customSelectSheetTheme ?? customTheme}
                    LoadingIconComponentProp={LoadingIconComponentProp}
                    isWorkWithRealtime={isWorkWithRealtime}
                    mainSelectedItems={mainSelectedItems}
                    moreLoadThreshold={moreLoadThreshold}
                    isShowTools={isShowSelectSheetTools}
                    isMultipleSelect={isMultipleSelect}
                    renderOptionIcon={renderOptionIcon}
                    setIsMoreLoading={setIsMoreLoading}
                    bottomSheetProps={selectSheetProps}
                    selectBoxKey={selectBoxKey.current}
                    setIsLoading={setIsListLoading}
                    bottomSheetRef={bottomSheetRef}
                    setSearchText={setSearchText}
                    isMoreLoading={isMoreLoading}
                    groupExtractor={getItemGroup}
                    searchedData={searchedData}
                    isSearchable={isSearchable}
                    keyExtractor={keyExtractor}
                    selectObject={selectObject}
                    isLoading={isListLoading}
                    setIsActive={setIsActive}
                    searchText={searchText}
                    renderItem={renderItem}
                    onMoreLoad={onMoreLoad}
                    maxChoice={maxChoice}
                    minChoice={minChoice}
                    selectAll={selectAll}
                    clearAll={clearAll}
                    isOpen={isActive}
                    cancel={cancel}
                    title={title}
                    data={data}
                    ok={ok}
                />
            :
                <SelectPopover
                    customLocalize={customSelectSheetLocalize ?? customLocalize}
                    isClearDisabled={!mainSelectedItems.length || !!minChoice}
                    isMaxChoiceReached={currentSelection.isMaxChoiceReached}
                    customTheme={customSelectSheetTheme ?? customTheme}
                    isMinChoiceMet={currentSelection.isMinChoiceMet}
                    mainSelectedItems={mainSelectedItems}
                    moreLoadThreshold={moreLoadThreshold}
                    isShowTools={isShowSelectSheetTools}
                    onSearchKeyPress={onSearchKeyPress}
                    renderOptionIcon={renderOptionIcon}
                    setIsMoreLoading={setIsMoreLoading}
                    isMultipleSelect={isMultipleSelect}
                    setIsLoading={setIsListLoading}
                    onSelect={handleSelectOption}
                    setSearchText={setSearchText}
                    isMoreLoading={isMoreLoading}
                    onActiveChange={setActiveKey}
                    groupExtractor={getItemGroup}
                    returnFocusRef={triggerRef}
                    isSearchable={isSearchable}
                    getOptionID={getOptionID}
                    isLoading={isListLoading}
                    searchText={searchText}
                    renderItem={renderItem}
                    onMoreLoad={onMoreLoad}
                    isOpen={isPopoverOpen}
                    onApply={handleApply}
                    maxChoice={maxChoice}
                    listboxID={listboxID}
                    activeKey={activeKey}
                    anchorRef={anchorRef}
                    searchRef={searchRef}
                    onClear={clearAll}
                    options={options}
                    onDismiss={blur}
                    title={title}
                    data={data}
                />
        }
    </Fragment>;
};
export default forwardRef(SelectBox) as unknown as SelectBoxComponent;
